import { v4 as uuidv4 } from 'uuid';
import { baselightTheme } from '@shared/theme/DefaultColorsV2';
import { TaskEditorValue, TaskFlowTaskDto, TaskFlowTaskType } from './contracts';

const taskTypeChartColors = baselightTheme.palette!.chart!;

export type TaskTypeFieldSet = 'none' | 'collectData' | 'dtmf';

export type TaskTypeMeta = {
  type: TaskFlowTaskType;
  labelKey: string;
  shortLabelKey: string;
  descriptionKey: string;
  color: string;
  fieldSet: TaskTypeFieldSet;
  defaultDescription: string;
};

export const TASK_TYPE_REGISTRY: Record<TaskFlowTaskType, TaskTypeMeta> = {
  collect_data: {
    type: 'collect_data',
    labelKey: 'taskTypes.collect_data.label',
    shortLabelKey: 'taskTypes.collect_data.shortLabel',
    descriptionKey: 'taskFlowDescriptions.collect_data',
    color: taskTypeChartColors[0],
    fieldSet: 'collectData',
    defaultDescription: 'Collect structured data from the caller',
  },
  get_email: {
    type: 'get_email',
    labelKey: 'taskTypes.get_email.label',
    shortLabelKey: 'taskTypes.get_email.shortLabel',
    descriptionKey: 'taskFlowDescriptions.get_email',
    color: taskTypeChartColors[2],
    fieldSet: 'none',
    defaultDescription: "Collect the caller's email address",
  },
  get_phone: {
    type: 'get_phone',
    labelKey: 'taskTypes.get_phone.label',
    shortLabelKey: 'taskTypes.get_phone.shortLabel',
    descriptionKey: 'taskFlowDescriptions.get_phone',
    color: taskTypeChartColors[4],
    fieldSet: 'none',
    defaultDescription: "Collect the caller's phone number",
  },
  get_name: {
    type: 'get_name',
    labelKey: 'taskTypes.get_name.label',
    shortLabelKey: 'taskTypes.get_name.shortLabel',
    descriptionKey: 'taskFlowDescriptions.get_name',
    color: taskTypeChartColors[6],
    fieldSet: 'none',
    defaultDescription: "Collect the caller's name",
  },
  get_dob: {
    type: 'get_dob',
    labelKey: 'taskTypes.get_dob.label',
    shortLabelKey: 'taskTypes.get_dob.shortLabel',
    descriptionKey: 'taskFlowDescriptions.get_dob',
    color: taskTypeChartColors[9],
    fieldSet: 'none',
    defaultDescription: "Collect the caller's date of birth",
  },
  get_address: {
    type: 'get_address',
    labelKey: 'taskTypes.get_address.label',
    shortLabelKey: 'taskTypes.get_address.shortLabel',
    descriptionKey: 'taskFlowDescriptions.get_address',
    color: taskTypeChartColors[11],
    fieldSet: 'none',
    defaultDescription: "Collect the caller's address",
  },
  get_dtmf: {
    type: 'get_dtmf',
    labelKey: 'taskTypes.get_dtmf.label',
    shortLabelKey: 'taskTypes.get_dtmf.shortLabel',
    descriptionKey: 'taskFlowDescriptions.get_dtmf',
    color: taskTypeChartColors[8],
    fieldSet: 'dtmf',
    defaultDescription: 'Collect keypad input from the caller',
  },
};

export const TASK_TYPES: TaskFlowTaskType[] = Object.keys(TASK_TYPE_REGISTRY) as TaskFlowTaskType[];

let taskKeySeq = 0;
export const nextTaskKey = (): string => `task-${Date.now()}-${(taskKeySeq += 1)}`;

export const createTaskId = (
  type: TaskFlowTaskType,
  flowIndex: number,
  existingIds: ReadonlySet<string>,
): string => {
  let suffix = Math.max(1, flowIndex);
  let id = `${type}_${suffix}`;
  while (existingIds.has(id)) {
    id = `${type}_${++suffix}`;
  }
  return id;
};

export const createEmptyTask = (
  type: TaskFlowTaskType,
  description?: string,
  id = '',
): TaskEditorValue => {
  const meta = TASK_TYPE_REGISTRY[type];
  const clientKey = nextTaskKey();
  const base: TaskEditorValue = {
    clientKey,
    id,
    type,
    description: description ?? meta.defaultDescription,
  };

  if (meta.fieldSet === 'dtmf') {
    return {
      ...base,
      askForConfirmation: false,
      dtmfInputTimeout: 4,
      dtmfStopEvent: '#',
    };
  }
  if (meta.fieldSet === 'collectData') {
    return {
      ...base,
      schemaType: 'json_schema',
      schema: { properties: {}, required: [] },
    };
  }
  return base;
};

export const normalizeTask = (raw: TaskFlowTaskDto): TaskEditorValue => {
  return {
    clientKey: nextTaskKey(),
    ...raw,
  };
};

export const createTaskFlowId = (): string => uuidv4();
